'use client';

import { create } from 'zustand';
import type { ActiveMode } from '@modules/garden-planner/models';

interface GardenPlannerStore {
  // ---- Editor modes ----
  selectedPlant: string | null;
  isAvailabilityMode: boolean;
  isCellSettingsMode: boolean;
  isPlantSelectionMode: boolean;
  isClearMode: boolean;

  // ---- Cell selection ----
  selectedCell: { row: number; col: number } | null;
  slotsPerCell: number;

  // ---- Bulk edit ----
  isBulkEditMode: boolean;
  bulkSelectedCells: Array<{ row: number; col: number }>;

  // ---- UI ----
  copySuccess: boolean;
  isSidebarOpen: boolean;

  // ---- Actions - Editor modes ----
  setSelectedPlant: (plant: string | null) => void;
  setIsAvailabilityMode: (mode: boolean | ((prev: boolean) => boolean)) => void;
  setIsCellSettingsMode: (mode: boolean | ((prev: boolean) => boolean)) => void;
  setIsPlantSelectionMode: (mode: boolean | ((prev: boolean) => boolean)) => void;
  setIsClearMode: (mode: boolean | ((prev: boolean) => boolean)) => void;
  setActiveMode: (mode: ActiveMode | null) => void;

  // ---- Actions - Cell selection ----
  setSelectedCell: (cell: { row: number; col: number } | null) => void;
  setSlotsPerCell: (slots: number) => void;

  // ---- Actions - Bulk edit ----
  setBulkEditMode: (enabled: boolean) => void;
  toggleBulkSelectedCell: (row: number, col: number) => void;
  clearBulkSelectedCells: () => void;

  // ---- Actions - UI ----
  setCopySuccess: (success: boolean) => void;
  setIsSidebarOpen: (isOpen: boolean) => void;
}

export const useGardenPlannerStore = create<GardenPlannerStore>((set) => ({
  selectedPlant: null,
  isAvailabilityMode: false,
  isCellSettingsMode: false,
  isPlantSelectionMode: false,
  isClearMode: false,

  selectedCell: null,
  slotsPerCell: 1,

  isBulkEditMode: false,
  bulkSelectedCells: [],

  copySuccess: false,
  isSidebarOpen: false,

  setSelectedPlant: (plant) => set({ selectedPlant: plant }),
  setIsAvailabilityMode: (mode) =>
    set((state) => ({
      isAvailabilityMode: typeof mode === 'function' ? mode(state.isAvailabilityMode) : mode,
    })),
  setIsCellSettingsMode: (mode) =>
    set((state) => ({
      isCellSettingsMode: typeof mode === 'function' ? mode(state.isCellSettingsMode) : mode,
    })),
  setIsPlantSelectionMode: (mode) =>
    set((state) => ({
      isPlantSelectionMode: typeof mode === 'function' ? mode(state.isPlantSelectionMode) : mode,
    })),
  setIsClearMode: (mode) =>
    set((state) => ({
      isClearMode: typeof mode === 'function' ? mode(state.isClearMode) : mode,
    })),
  setActiveMode: (mode) =>
    set((state) => {
      const wasPlant = state.isPlantSelectionMode;
      const wasAvailability = state.isAvailabilityMode;
      const wasCellSettings = state.isCellSettingsMode;

      const shouldClearPlant = (wasPlant && mode !== 'plant') || mode === 'clear';
      const shouldClearCell =
        (wasAvailability && mode !== 'availability') ||
        (wasCellSettings && mode !== 'cellSettings') ||
        mode === 'plant' ||
        mode === 'clear';
      const leavingCellSettings = wasCellSettings && mode !== 'cellSettings';

      return {
        isPlantSelectionMode: mode === 'plant',
        isAvailabilityMode: mode === 'availability',
        isCellSettingsMode: mode === 'cellSettings',
        isClearMode: mode === 'clear',
        ...(shouldClearPlant && { selectedPlant: null }),
        ...(shouldClearCell && { selectedCell: null }),
        ...(leavingCellSettings && { isBulkEditMode: false, bulkSelectedCells: [] }),
      };
    }),

  setSelectedCell: (cell) => set({ selectedCell: cell }),
  setSlotsPerCell: (slots) => set({ slotsPerCell: slots }),

  setBulkEditMode: (enabled) =>
    set(() => ({
      isBulkEditMode: enabled,
      bulkSelectedCells: [],
      ...(!enabled ? { selectedCell: null } : {}),
    })),
  toggleBulkSelectedCell: (row, col) =>
    set((state) => {
      const exists = state.bulkSelectedCells.some((c) => c.row === row && c.col === col);
      return {
        bulkSelectedCells: exists
          ? state.bulkSelectedCells.filter((c) => !(c.row === row && c.col === col))
          : [...state.bulkSelectedCells, { row, col }],
      };
    }),
  clearBulkSelectedCells: () => set({ bulkSelectedCells: [] }),

  setCopySuccess: (success) => set({ copySuccess: success }),
  setIsSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),
}));
